import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, MapPin, Sparkles, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SpeechBubble } from '../components/ui/SpeechBubble';
import { Badge } from '../components/ui/Badge';
import { FloatingDecor } from '../components/ui/FloatingDecor';
const WEB3FORMS_ACCESS_KEY = 'c04bd522-f6a1-4867-bc71-64a921a3c289';
const sectionEntrance = {
  initial: {
    opacity: 0,
    y: 40,
    scale: 0.96
  },
  whileInView: {
    opacity: 1,
    y: 0,
    scale: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    type: 'spring',
    stiffness: 200,
    damping: 20
  }
};
const popWord = {
  initial: {
    opacity: 0,
    scale: 0.8,
    rotate: 0
  },
  animate: {
    opacity: 1,
    scale: 1,
    rotate: -2
  },
  transition: {
    type: 'spring',
    stiffness: 300,
    damping: 15,
    delay: 0.3
  }
};
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const location = useLocation();
  const [type, setType] = useState<'brand' | 'talent'>('brand');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    social: '',
    message: ''
  });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');
    if (typeParam === 'talent') {
      setType('talent');
    }
  }, [location]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `Nouveau message JANY & CO — ${type === 'brand' ? 'Marque' : 'Talent'}`,
        from_name: 'Site JANY & CO',
        type_de_demande: type === 'brand' ? 'Marque' : 'Talent',
        name: formData.name,
        email: formData.email,
        ...(type === 'brand' ? {
          entreprise: formData.company
        } : {
          reseaux_sociaux: formData.social
        }),
        message: formData.message
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (result.success) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          social: '',
          message: ''
        });
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };
  return <div className="pt-32 pb-0 overflow-hidden" data-id="element-136">
      <div className="container mx-auto px-6 md:px-12 mb-20 text-center relative" data-id="element-137">
        <FloatingDecor color="pink" count={4} data-id="element-138" />
        <div className="relative z-10" data-id="element-139">
          <SpeechBubble text="LET'S TALK" color="yellow" className="mb-8" data-id="element-140" />
          <h1 className="text-6xl md:text-8xl mb-6 text-jany-dark" data-id="element-141">
            Contactez-
            <motion.span variants={popWord} initial="initial" animate="animate" className="inline-block text-jany-blue italic" data-id="element-142">
              nous
            </motion.span>
          </h1>
        </div>
      </div>

      <motion.section {...sectionEntrance} className="bg-jany-pink py-24 rounded-t-[4rem] relative" data-id="element-143">
        <FloatingDecor color="white" count={10} data-id="element-144" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-145">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-12" data-id="element-146">
            {/* Left Panel - Instagram Card Mockup */}
            <div className="lg:w-2/5" data-id="element-147">
              <motion.div whileHover={{
              rotate: 0,
              scale: 1.02
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 15
            }} className="bg-white rounded-[3rem] p-8 shadow-2xl border-8 border-jany-pink-pale rotate-[-2deg]" data-id="element-148">
                <div className="flex items-center gap-6 mb-8" data-id="element-149">
                  <div className="w-24 h-24 rounded-full bg-jany-yellow flex items-center justify-center overflow-hidden border-4 border-white shadow-sm" data-id="element-150">
                    <Sparkles className="text-jany-pink" size={36} data-id="element-151" />
                  </div>
                  <div data-id="element-152">
                    <h2 className="text-2xl font-bold text-jany-dark" data-id="element-153">
                      janyagency
                    </h2>
                    <div className="flex gap-4 mt-2" data-id="element-154">
                      <div className="text-center" data-id="element-155">
                        <span className="font-bold block text-jany-dark" data-id="element-156">
                          245
                        </span>
                        <span className="text-xs text-jany-dark/60 uppercase" data-id="element-157">
                          Posts
                        </span>
                      </div>
                      <div className="text-center" data-id="element-158">
                        <span className="font-bold block text-jany-dark" data-id="element-159">
                          12.4k
                        </span>
                        <span className="text-xs text-jany-dark/60 uppercase" data-id="element-160">
                          Followers
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-8" data-id="element-161">
                  <h3 className="font-bold text-jany-dark text-lg" data-id="element-162">
                    JANY & CO
                  </h3>
                  <p className="text-jany-dark/70 font-medium" data-id="element-163">
                    Talent & Influence Agency
                  </p>
                  <p className="text-jany-dark/70 font-medium" data-id="element-164">
                    We connect brands with the right talents ✨
                  </p>
                  <p className="text-jany-dark/70 font-medium flex items-center gap-2 mt-2" data-id="element-165">
                    <MapPin size={16} className="text-jany-orange" data-id="element-166" /> Paris |
                    Worldwide
                  </p>
                </div>

                <div className="flex gap-4" data-id="element-167">
                  <motion.a whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} href="https://instagram.com/janyagency" target="_blank" rel="noopener noreferrer" className="flex-1 bg-jany-cream text-jany-dark font-bold py-3 rounded-xl text-center hover:bg-jany-yellow transition-colors" data-id="element-168">
                    Follow
                  </motion.a>
                  <motion.a whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} href="mailto:janyagency.m@gmail.com" className="flex-1 bg-jany-dark text-white font-bold py-3 rounded-xl text-center hover:bg-jany-pink transition-colors" data-id="element-169">
                    Message
                  </motion.a>
                </div>

                <div className="mt-8 flex justify-around border-t border-jany-cream pt-6" data-id="element-170">
                  <Badge variant="rose-sparkle" size="sm" data-id="element-171" />
                  <Badge variant="yellow-star" size="sm" data-id="element-172" />
                  <Badge variant="blue-palmtree" size="sm" data-id="element-173" />
                  <Badge variant="lightrose-eye" size="sm" data-id="element-174" />
                </div>
              </motion.div>
            </div>

            {/* Right Form Panel */}
            <div className="lg:w-3/5 bg-white rounded-[3rem] p-10 md:p-16 shadow-2xl" data-id="element-175">
              <h2 className="text-4xl mb-10 font-display text-jany-dark" data-id="element-176">
                Envoyez-nous un message
              </h2>

              {/* Type Selector */}
              <div className="flex p-2 bg-jany-cream rounded-full mb-10 w-full" data-id="element-177">
                <button type="button" onClick={() => setType('brand')} className={`flex-1 py-4 px-6 rounded-full text-lg font-bold transition-all ${type === 'brand' ? 'bg-jany-orange shadow-md text-white' : 'text-jany-dark/50 hover:text-jany-dark'}`} data-id="element-178">
                  Je suis une marque
                </button>
                <button type="button" onClick={() => setType('talent')} className={`flex-1 py-4 px-6 rounded-full text-lg font-bold transition-all ${type === 'talent' ? 'bg-jany-blue shadow-md text-jany-dark' : 'text-jany-dark/50 hover:text-jany-dark'}`} data-id="element-179">
                  Je suis un talent
                </button>
              </div>

              {status === 'success' ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex flex-col items-center text-center py-12" data-id="element-180">
                  <CheckCircle className="text-jany-orange mb-6" size={64} data-id="element-181" />
                  <h3 className="text-3xl font-display text-jany-dark mb-3" data-id="element-182">
                    Message envoyé !
                  </h3>
                  <p className="text-jany-dark/70 font-medium text-lg mb-8" data-id="element-183">
                    Merci, on revient vers vous très vite.
                  </p>
                  <Button variant="outline" size="md" onClick={() => setStatus('idle')} data-id="element-184">
                    Envoyer un autre message
                  </Button>
                </motion.div> : <form className="space-y-8" onSubmit={handleSubmit} data-id="element-185">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-186">
                    <div data-id="element-187">
                      <label className="block text-sm font-bold text-jany-dark mb-3 uppercase tracking-wider" data-id="element-188">
                        Prénom & Nom *
                      </label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-6 py-5 rounded-2xl bg-jany-cream border-2 border-transparent focus:border-jany-pink focus:bg-white focus:ring-0 transition-all outline-none font-medium text-lg" placeholder="Jane Doe" required data-id="element-189" />
                    </div>
                    <div data-id="element-190">
                      <label className="block text-sm font-bold text-jany-dark mb-3 uppercase tracking-wider" data-id="element-191">
                        Email *
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-6 py-5 rounded-2xl bg-jany-cream border-2 border-transparent focus:border-jany-pink focus:bg-white focus:ring-0 transition-all outline-none font-medium text-lg" placeholder="jane@example.com" required data-id="element-192" />
                    </div>
                  </div>

                  {type === 'brand' ? <div data-id="element-193">
                      <label className="block text-sm font-bold text-jany-dark mb-3 uppercase tracking-wider" data-id="element-194">
                        Nom de l'entreprise / Marque *
                      </label>
                      <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full px-6 py-5 rounded-2xl bg-jany-cream border-2 border-transparent focus:border-jany-orange focus:bg-white focus:ring-0 transition-all outline-none font-medium text-lg" placeholder="Votre marque" required data-id="element-195" />
                    </div> : <div data-id="element-196">
                      <label className="block text-sm font-bold text-jany-dark mb-3 uppercase tracking-wider" data-id="element-197">
                        Lien vers vos réseaux sociaux *
                      </label>
                      <input type="url" name="social" value={formData.social} onChange={handleChange} className="w-full px-6 py-5 rounded-2xl bg-jany-cream border-2 border-transparent focus:border-jany-blue focus:bg-white focus:ring-0 transition-all outline-none font-medium text-lg" placeholder="https://instagram.com/..." required data-id="element-198" />
                    </div>}

                  <div data-id="element-199">
                    <label className="block text-sm font-bold text-jany-dark mb-3 uppercase tracking-wider" data-id="element-200">
                      Votre message *
                    </label>
                    <textarea rows={5} name="message" value={formData.message} onChange={handleChange} className={`w-full px-6 py-5 rounded-2xl bg-jany-cream border-2 border-transparent focus:bg-white focus:ring-0 transition-all outline-none resize-none font-medium text-lg ${type === 'brand' ? 'focus:border-jany-orange' : 'focus:border-jany-blue'}`} placeholder="Parlez-nous de votre projet..." required data-id="element-201"></textarea>
                  </div>

                  {status === 'error' && <div className="flex items-center gap-3 bg-red-50 text-red-600 px-6 py-4 rounded-2xl font-medium" data-id="element-202">
                      <AlertCircle size={20} className="shrink-0" data-id="element-203" />
                      Une erreur est survenue. Réessayez ou écrivez-nous directement à
                      janyagency.m@gmail.com.
                    </div>}

                  <Button type="submit" variant={type === 'brand' ? 'orange' : 'blue'} size="lg" className="w-full text-xl py-5 shadow-lg" disabled={status === 'submitting'} data-id="element-204">
                    {status === 'submitting' ? <>
                        Envoi en cours...{' '}
                        <Loader2 size={20} className="ml-3 animate-spin" data-id="element-205" />
                      </> : <>
                        Envoyer le message <Send size={20} className="ml-3" data-id="element-206" />
                      </>}
                  </Button>
                </form>}
            </div>
          </div>
        </div>
      </motion.section>
    </div>;
}